/**
 * Channels Page
 * 
 * List all group channels with search, filter, and create functionality.
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Hash, 
  Plus, 
  Search, 
  Users, 
  Briefcase,
  Lock,
  Globe,
  ChevronRight,
  Loader2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn, formatRelativeTime, getInitials, getAvatarColor } from '../lib/utils'
import { channelsApi, usersApi, type Channel } from '../lib/api'
import { useTeamsStore } from '../store/teamsStore'

interface ChannelsPageProps {
  showCreate?: boolean
}

export default function ChannelsPage({ showCreate = false }: ChannelsPageProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(showCreate)
  const [filter, setFilter] = useState<'all' | 'job' | 'group'>('all')
  const { setChannels } = useTeamsStore()
  
  // Fetch channels
  const { data: channels = [], isLoading } = useQuery({
    queryKey: ['teams-channels'],
    queryFn: channelsApi.list,
    onSuccess: setChannels,
  })
  
  // Filter channels
  const filteredChannels = useMemo(() => {
    let result = channels.filter(c => c.type !== 'direct')
    
    if (filter === 'job') {
      result = result.filter(c => c.job_id)
    } else if (filter === 'group') {
      result = result.filter(c => !c.job_id)
    }
    
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      result = result.filter(c => c.name.toLowerCase().includes(q))
    }
    
    return result
  }, [channels, filter, searchQuery])
  
  // Group by pinned/unpinned
  const pinnedChannels = filteredChannels.filter(c => c.is_pinned)
  const regularChannels = filteredChannels.filter(c => !c.is_pinned)

  return (
    <div className="h-full bg-slate-50 dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">Channels</h1>
            <p className="text-slate-500 dark:text-slate-400">
              {channels.filter(c => c.type !== 'direct').length} channels
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Channel
          </button>
        </div>
        
        {/* Search & Filter */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search channels..."
              className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-1">
            {(['all', 'group', 'job'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  filter === f
                    ? "bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                )}
              >
                {f === 'all' ? 'All' : f === 'job' ? 'Job Linked' : 'General'}
              </button>
            ))}
          </div>
        </div>
        
        {/* Channel List */}
        {isLoading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-500 mb-3" />
            <p className="text-sm text-slate-400">Loading channels...</p>
          </div>
        ) : filteredChannels.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
            <Hash className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
            <p className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">No channels found</p>
            <p className="text-sm text-slate-400 mb-4">
              {searchQuery ? 'Try a different search term' : 'Create your first channel to start collaborating'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg font-medium transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Channel
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Pinned */}
            {pinnedChannels.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                  Pinned
                </h3>
                <div className="space-y-2">
                  {pinnedChannels.map((channel) => (
                    <ChannelCard key={channel.id} channel={channel} onClick={() => navigate(`/channels/${channel.id}`)} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Regular */}
            <div>
              {pinnedChannels.length > 0 && (
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-1">
                  All Channels
                </h3>
              )}
              <div className="space-y-2">
                {regularChannels.map((channel) => (
                  <ChannelCard key={channel.id} channel={channel} onClick={() => navigate(`/channels/${channel.id}`)} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Create Modal */}
      {showCreateModal && (
        <CreateChannelModal
          onClose={() => {
            setShowCreateModal(false)
            if (showCreate) navigate('/channels')
          }}
          onCreate={(channel) => {
            navigate(`/channels/${channel.id}`)
          }}
        />
      )}
    </div>
  )
}

// Channel Card Component
function ChannelCard({ channel, onClick }: { channel: Channel, onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-violet-300 dark:hover:border-violet-700 hover:shadow-md transition-all text-left"
    >
      <div className={cn(
        "w-12 h-12 rounded-xl flex items-center justify-center",
        channel.job_id 
          ? "bg-blue-100 dark:bg-blue-900/30" 
          : "bg-violet-100 dark:bg-violet-900/30"
      )}>
        {channel.job_id ? (
          <Briefcase className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        ) : (
          <Hash className="w-6 h-6 text-violet-600 dark:text-violet-400" />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-slate-800 dark:text-white truncate">{channel.name}</h3>
          {channel.job_id && (
            <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
              Job #{channel.job_id}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Users className="w-3 h-3" />
            {channel.member_count} members
          </span>
          {channel.last_message && (
            <span className="text-xs text-slate-400 truncate">
              Last message {formatRelativeTime(channel.last_message.created_at)}
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {channel.unread_count > 0 && (
          <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-violet-500 text-white">
            {channel.unread_count}
          </span>
        )}
        <ChevronRight className="w-5 h-5 text-slate-400" />
      </div>
    </button>
  )
}

// Create Channel Modal
function CreateChannelModal({ 
  onClose, 
  onCreate 
}: { 
  onClose: () => void
  onCreate: (channel: Channel) => void 
}) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  
  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Channel name is required')
      return
    }
    
    setIsCreating(true)
    try {
      const channel = await channelsApi.create({
        type: 'group',
        name: name.trim(),
        description: description.trim(),
      })
      queryClient.invalidateQueries(['teams-channels'])
      toast.success('Channel created!')
      onCreate(channel)
    } catch (e) {
      toast.error('Failed to create channel')
    } finally {
      setIsCreating(false)
    }
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white">Create Channel</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Channel Name
            </label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. general, marketing"
                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                autoFocus
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Description <span className="text-slate-400">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this channel about?"
              rows={3}
              className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
            />
          </div>
        </div>
        
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isCreating || !name.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-slate-400 text-white rounded-lg font-medium transition-colors"
          >
            {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Channel
          </button>
        </div>
      </div>
    </div>
  )
}
